<template>
  <div>
    <certifications-terms-list-filters
      :day-filter-from.sync="dayFilterFrom"
      :day-filter-to.sync="dayFilterTo"
      :kind-filter.sync="kindFilter"
      :department-filter.sync="departmentFilter"
      :examiner-filter.sync="examinerFilter"
      :localization-filter.sync="localizationFilter"
      :status-filter.sync="statusFilter"
      :examiner-options="examinerOptions"
    />

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Pokaż</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>terminów</label>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-md-end"
          >
            <b-button
              v-if="isAdmin"
              variant="gradient-primary"
              to="/certifications-terms/new"
            >
              <span class="text-nowrap">Dodaj termin</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refCertificationsTermsTable"
        class="position-relative"
        :items="filteredCertifications"
        responsive
        :fields="visibleTableColumns"
        primary-key="id"
        show-empty
        empty-text="Nie znaleziono terminów certyfikacji spełniających podane kryteria."
        :busy="loading"
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(day)="{ item }">
          <span style="white-space: nowrap">{{ item.date | date }}</span>
        </template>

        <template #cell(kind)="{ item }">
          {{ resolveCertificationKind(item.kind) }}
        </template>

        <template #cell(departments)="{ item }">
          {{ resolveDepartments(item.departments) }}
        </template>

        <template #cell(examiner)="{ item }">
          <span v-if="item.examiner">{{ item.examiner.firstName }} {{ item.examiner.lastName }}</span>
          <span v-else>-</span>
        </template>

        <template #cell(places)="{ item }">
          {{ item.availableSlotsCount }}
        </template>

        <template #cell(enrolled)="{ item }">
          {{ item.takenSlotsCount }}
        </template>

        <template #cell(status)="{ item }">
          <b-badge
            pill
            :variant="`light-${resolveCertificationStatus(item.status).variant}`"
          >
            {{ resolveCertificationStatus(item.status).text.toUpperCase() }}
          </b-badge>
        </template>

        <template #cell(actions)="{ item }">
          <div style="white-space: nowrap">
            <b-button-group>
              <b-button
                size="sm"
                variant="light"
                :to="`/certifications/detail/${item.id}`"
              >
                <feather-icon icon="InfoIcon" />
                <span class="align-middle ml-50">Szczegóły</span>
              </b-button>
              <b-button
                v-if="isAdmin"
                size="sm"
                variant="light"
                :to="`/certifications-terms/edit/${item.id}`"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Edytuj</span>
              </b-button>
              <b-button
                v-if="isAdmin"
                size="sm"
                variant="light"
                :disabled="!canDeleteCertificationTerm(item.takenSlotsCount)"
                @click="openDelete(item)"
              >
                <feather-icon icon="Trash2Icon" />
                <span class="align-middle ml-50">Usuń</span>
              </b-button>
            </b-button-group>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Widok {{ metadata.from }} - {{ metadata.to }} z {{ metadata.of }} terminów
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-modal
      v-if="deleteConfirmItem"
      v-model="showDeleteModal"
      centered
      modal-class="modal-danger"
      title="Potwierdź usunięcie"
      ok-variant="danger"
      cancel-variant="light"
      ok-title="Usuń"
      cancel-title="Anuluj"
      @ok.prevent="confirmDelete"
    >
      Na pewno usunąć certyfikację z dnia
      <strong>{{ deleteConfirmItem.date | date }}</strong>
      ({{ resolveCertificationKind(deleteConfirmItem.kind) }})?
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge, BPagination, BButton, BButtonGroup, BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import userRoleMixin from '@/mixins/userRoleMixin'
import visibleTableColumns from '@/helpers/visibleTableColumns'
import resolveDepartments from '@/helpers/resolveDepartments'
import resolveCertificationKind from '@/helpers/resolveCertificationKind'
import resolveCertificationStatus from '@/helpers/resolveCertificationStatus'
import {
  filterCertificationTermsList,
  canDeleteCertificationTerm,
} from '@/helpers/certification/certificationTermsListLogic'
import { buildTrainerSelectOptions } from '@/helpers/certification/certificationSelectOptions'
import CertificationsTermsListFilters from './CertificationsTermsListFilters.vue'

export default {
  components: {
    CertificationsTermsListFilters,
    BCard,
    BRow,
    BCol,
    BTable,
    BBadge,
    BPagination,
    BButton,
    BButtonGroup,
    BModal,
    vSelect,
  },
  mixins: [userRoleMixin],
  setup() {
    const tableColumns = [
      { key: 'day', sortable: true, label: 'Dzień' },
      { key: 'kind', sortable: true, label: 'Rodzaj' },
      { key: 'departments', label: 'Dział' },
      { key: 'examiner', label: 'Egzaminator' },
      { key: 'localization', sortable: true, label: 'Lokalizacja' },
      { key: 'places', sortable: true, label: 'Dostępne miejsca' },
      { key: 'enrolled', sortable: true, label: 'Zapisane osoby' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Akcje' },
    ]
    const perPage = ref(10)
    const currentPage = ref(1)
    const perPageOptions = [10, 25, 50, 100]
    return {
      tableColumns,
      perPage,
      currentPage,
      perPageOptions,
    }
  },
  data() {
    return {
      loading: false,
      dayFilterFrom: null,
      dayFilterTo: null,
      kindFilter: null,
      departmentFilter: null,
      examinerFilter: null,
      localizationFilter: '',
      statusFilter: null,
      deleteConfirmItem: null,
      showDeleteModal: false,
    }
  },
  computed: {
    visibleTableColumns,
    certifications() {
      const raw = this.$store.state.certification.list
        ? this.$store.state.certification.list['hydra:member']
        : []
      return Array.isArray(raw) ? raw : []
    },
    filteredCertifications() {
      return filterCertificationTermsList(this.certifications, {
        dayFilterFrom: this.dayFilterFrom,
        dayFilterTo: this.dayFilterTo,
        kindFilter: this.kindFilter,
        departmentFilter: this.departmentFilter,
        examinerFilter: this.examinerFilter,
        localizationFilter: this.localizationFilter,
        statusFilter: this.statusFilter,
      })
    },
    examinerOptions() {
      const users = this.$store.state.user.list
        ? this.$store.state.user.list['hydra:member']
        : []
      return buildTrainerSelectOptions(users)
    },
    totalRows() {
      return this.filteredCertifications.length
    },
    metadata() {
      const localItemsCount = this.$refs.refCertificationsTermsTable
        ? this.$refs.refCertificationsTermsTable.localItems.length
        : 0
      return {
        from: this.perPage * (this.currentPage - 1) + (localItemsCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localItemsCount,
        of: this.totalRows,
      }
    },
  },
  watch: {
    dayFilterFrom() { this.currentPage = 1 },
    dayFilterTo() { this.currentPage = 1 },
    kindFilter() { this.currentPage = 1 },
    departmentFilter() { this.currentPage = 1 },
    examinerFilter() { this.currentPage = 1 },
    localizationFilter() { this.currentPage = 1 },
    statusFilter() { this.currentPage = 1 },
    perPage() { this.currentPage = 1 },
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    resolveDepartments,
    resolveCertificationKind,
    resolveCertificationStatus,
    canDeleteCertificationTerm,
    fetchData() {
      this.loading = true
      Promise.all([
        this.$store.dispatch('certification/fetchList', {
          params: {
            pagination: false,
            'order[date]': 'desc',
          },
        }),
        this.$store.dispatch('user/fetchList', { params: { roles: 'ROLE_TRAINER' } }),
      ]).finally(() => {
        this.loading = false
      })
    },
    openDelete(item) {
      this.deleteConfirmItem = { ...item }
      this.showDeleteModal = true
    },
    confirmDelete() {
      const item = this.deleteConfirmItem
      if (!item) return
      this.$store.dispatch('certification/deleteCertification', { id: item.id })
        .then(response => {
          if (response && response.status === 204) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Termin certyfikacji został usunięty',
                icon: 'Trash2Icon',
                variant: 'success',
              },
            })
            this.fetchData()
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Nie udało się usunąć terminu.',
                text: response?.data?.['hydra:description'],
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
              timeout: false,
            })
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się usunąć terminu.',
              text: error?.response?.data?.['hydra:description'] || error?.data?.['hydra:description'],
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
            timeout: false,
          })
        })
        .finally(() => {
          this.deleteConfirmItem = null
          this.showDeleteModal = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
